import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { AppUserDto } from './app-user.model';
import { SupabaseService } from './supabase.service';
import { TenantFeatureService } from '../tenant/tenant-feature.service';
import { KannNutzenConfig } from '../tenant/tenant-feature.model';
import { DEV_JWT_KEY } from './dev-auth.constants';

/**
 * Zentraler Auth-Service fuer den Production Planner / Polier AI.
 *
 * Unterstuetzt Dual-Auth:
 * - Azure AD (MSAL) fuer Enterprise-Kunden (LIGNA)
 * - Supabase Auth fuer Self-Service-Kunden (Polier AI)
 *
 * Rollen kommen ausschliesslich aus dem Backend (`/api/users/me`),
 * NICHT aus dem JWT-Token. Die DB ist Single Source of Truth.
 *
 * Flow:
 *  1. Login via MSAL oder Supabase → Token im Cache
 *  2. roleGuard ruft `ladeUser()` → GET /api/users/me → AppUserDto gecacht
 *  3. `hasRole()` liest aus dem Cache – kein weiterer API-Call noetig
 */
@Injectable({ providedIn: 'root' })
export class AuthService {

  private readonly _currentUser$ = new BehaviorSubject<AppUserDto | null>(null);

  /** Observable auf den aktuell geladenen AppUser (null = noch nicht geladen). */
  readonly currentUser$ = this._currentUser$.asObservable();

  /** Promise das laeuft solange `ladeUser()` in Bearbeitung ist. Verhindert parallele Calls. */
  private _ladePromise: Promise<void> | null = null;

  /** Welcher Auth-Provider aktuell aktiv ist. */
  private _authProvider: 'msal' | 'supabase' | null = null;

  /** True wenn die API nicht erreichbar ist (Netzwerkfehler, Backend offline). */
  private _apiNichtErreichbar = false;

  /** Fehlermeldung bei API-Verbindungsproblemen. */
  private _apiFehlermeldung: string | null = null;

  private readonly featureService = inject(TenantFeatureService);

  constructor(
    private msalService: MsalService,
    private supabaseService: SupabaseService,
    private http: HttpClient
  ) {}

  /**
   * Emittiert jedes Mal wenn ein AppUser erfolgreich geladen wurde.
   * Andere Services (z.B. ImpersonationService) koennen darauf reagieren,
   * um Folge-Daten zu laden – ohne dass der AuthService sie direkt kennen muss.
   */
  private readonly _userGeladen$ = new Subject<AppUserDto>();
  readonly userGeladen$ = this._userGeladen$.asObservable();

  /**
   * Laedt den aktuell angemeldeten AppUser vom Backend und speichert ihn im Cache.
   *
   * Erkennt automatisch ob der User via MSAL oder Supabase eingeloggt ist
   * und setzt den passenden Authorization-Header.
   */
  ladeUser(): Promise<void> {
    if (this._currentUser$.value !== null) {
      return Promise.resolve();
    }

    if (this._ladePromise) {
      return this._ladePromise;
    }

    this._ladePromise = this.erstelleAuthHeaders()
      .then(headers => {
        // Token-Erwerb komplett fehlgeschlagen → nicht als API-Fehler behandeln
        if (this._tokenFehler) {
          this._tokenFehler = false;
          return; // null bleibt → Guard leitet zu /login
        }
        return firstValueFrom(
          this.http.get<AppUserDto>(`${environment.apiUrl}/api/users/me`, { headers })
        ).then(user => {
          this._currentUser$.next(user);
          // Interessierte Services (z.B. ImpersonationService) benachrichtigen.
          // Der AuthService kennt deren Existenz bewusst nicht – damit bleibt
          // die Abhaengigkeit einseitig.
          this._userGeladen$.next(user);
        });
      })
      .catch((err: unknown) => {
        const status = (err instanceof Object && 'status' in err ? (err as { status: number }).status : 0) ?? 0;
        if (status === 0 || status >= 500) {
          // Netzwerkfehler oder Server-Fehler → Backend nicht erreichbar
          this._apiNichtErreichbar = true;
          this._apiFehlermeldung = status === 0
            ? 'Backend nicht erreichbar. Bitte pruefen ob der Server laeuft.'
            : `Server-Fehler (${status}). Bitte spaeter erneut versuchen.`;
        }
        // Bei 401/403/404: User nicht in DB → null bleibt, Guard leitet zu /login
      })
      .finally(() => {
        this._ladePromise = null;
      });

    return this._ladePromise;
  }

  /** Gibt an ob der AppUser bereits aus dem Backend geladen wurde. */
  get userGeladen(): boolean {
    return this._currentUser$.value !== null;
  }

  /** True wenn die API nicht erreichbar war (Netzwerk/Server-Fehler). */
  get apiNichtErreichbar(): boolean {
    return this._apiNichtErreichbar;
  }

  /** Fehlermeldung bei API-Verbindungsproblemen. */
  get apiFehlermeldung(): string | null {
    return this._apiFehlermeldung;
  }

  /**
   * Liest alle Rollen des aktuell angemeldeten Benutzers aus dem gecachten AppUser.
   * Rollen kommen aus der Datenbank via `/api/users/me`.
   */
  getRoles(): string[] {
    return this._currentUser$.value?.rollen ?? [];
  }

  /**
   * Prueft, ob der aktuelle Benutzer mindestens eine der angegebenen Rollen besitzt.
   */
  hasRole(rollen: string[]): boolean {
    if (!rollen || rollen.length === 0) {
      return true;
    }
    const userRollen = this.getRoles();
    return rollen.some(rolle => userRollen.includes(rolle));
  }

  /**
   * Prueft ob der aktuelle Benutzer die angegebene Berechtigung hat.
   * SystemAdmin/TenantAdmin haben implizit alle Berechtigungen.
   *
   * @param bereich Der Bereich (z.B. 'Dashboard', 'Planung')
   * @param aktion 'lesen' oder 'bearbeiten'
   */
  hatBerechtigung(bereich: string, aktion: 'lesen' | 'bearbeiten'): boolean {
    const user = this._currentUser$.value;
    if (!user) return false;

    // SystemAdmin/TenantAdmin haben implizit alle Berechtigungen
    if (user.rollen.includes('SystemAdmin') || user.rollen.includes('TenantAdmin')) {
      return true;
    }

    const berechtigung = user.berechtigungen?.find(b => b.bereich === bereich);
    if (!berechtigung) return false;

    if (aktion === 'bearbeiten') return berechtigung.bearbeiten;
    return berechtigung.lesen || berechtigung.bearbeiten;
  }

  /**
   * Prueft ob der aktuelle Benutzer eine Aktion ausfuehren darf.
   * Kombiniert Feature-Check, Rollen-Check und Berechtigungs-Check in einem Aufruf.
   *
   * Alle gesetzten Bedingungen muessen gleichzeitig erfuellt sein (AND-Logik).
   * Nicht gesetzte Felder werden ignoriert.
   *
   * @param config Konfiguration welche Checks durchgefuehrt werden sollen.
   * @returns true wenn alle gesetzten Bedingungen erfuellt sind, sonst false.
   *
   * @example
   * // Nur Feature
   * authService.kannNutzen({ feature: TenantFeatureKeys.Arbeitsbereiche })
   *
   * // Nur Rollen (OR: mindestens eine muss passen)
   * authService.kannNutzen({ rollen: ['TenantAdmin', 'Planer'] })
   *
   * // Feature + Rolle kombiniert (AND)
   * authService.kannNutzen({ feature: TenantFeatureKeys.Gantt, rollen: ['Planer'] })
   */
  kannNutzen(config: KannNutzenConfig): boolean {
    // Feature-Check: Feature muss aktiv sein
    if (config.feature !== undefined) {
      if (!this.featureService.istAktiv(config.feature)) return false;
    }
    // Rollen-Check: mind. eine Rolle muss passen (OR)
    if (config.rollen && config.rollen.length > 0) {
      if (!this.hasRole(config.rollen)) return false;
    }
    // Berechtigungs-Check
    if (config.berechtigung) {
      if (!this.hatBerechtigung(config.berechtigung.bereich, config.berechtigung.aktion)) return false;
    }
    return true;
  }

  /** Gibt den aktuell gecachten AppUser zurueck. */
  get currentUser(): AppUserDto | null {
    return this._currentUser$.value;
  }

  /** Laedt das vollstaendige AppUser-Profil vom Backend (Observable-Version). */
  getCurrentUser(): Observable<AppUserDto> {
    return this.http.get<AppUserDto>(`${environment.apiUrl}/api/users/me`);
  }

  /**
   * Gibt an, ob aktuell ein Benutzer eingeloggt ist (MSAL oder Supabase).
   * Prueft den aktiven Auth-Provider basierend auf authModus:
   * - 'email': Supabase zuerst (MSAL-Accounts im LocalStorage ignorieren)
   * - 'microsoft': MSAL zuerst
   */
  isLoggedIn(): boolean {
    // Dev-JWT: in non-Production als gültige Session behandeln, damit roleGuard
    // ladeUser() aufruft und den User mit Berechtigungen aus der DB lädt.
    // Ohne diesen Check bleibt currentUser null → alle Berechtigungsguards schlagen fehl.
    if (!environment.production && localStorage.getItem(DEV_JWT_KEY)) {
      return true;
    }

    if (environment.authModus === 'email') {
      // Email-Modus: Supabase hat Vorrang
      if (this.supabaseService.istEingeloggt) {
        this._authProvider = 'supabase';
        return true;
      }
      // Fallback: MSAL (falls der User doch via Microsoft eingeloggt ist)
      if (this.msalService.instance.getAllAccounts().length > 0) {
        this._authProvider = 'msal';
        return true;
      }
    } else {
      // Microsoft-Modus: MSAL hat Vorrang
      if (this.msalService.instance.getAllAccounts().length > 0) {
        this._authProvider = 'msal';
        return true;
      }
      if (this.supabaseService.istEingeloggt) {
        this._authProvider = 'supabase';
        return true;
      }
    }
    return false;
  }

  /** Gibt den aktiven Auth-Provider zurueck. */
  get authProvider(): 'msal' | 'supabase' | null {
    return this._authProvider;
  }

  /** Loescht den gecachten AppUser (z.B. bei Logout). */
  clearUser(): void {
    this._currentUser$.next(null);
    this._ladePromise = null;
    this._authProvider = null;
    this._apiNichtErreichbar = false;
    this._apiFehlermeldung = null;
    this._tokenFehler = false;
  }

  /** Logout — beendet die Session beim aktiven Provider. */
  async logout(): Promise<void> {
    const provider = this._authProvider;
    this.clearUser();
    if (provider === 'supabase') {
      await this.supabaseService.logout();
    } else {
      this.msalService.logoutRedirect();
    }
  }

  /**
   * Erstellt Auth-Headers fuer API-Calls.
   * Fuer Supabase-User: manueller Bearer-Token im Header.
   * Fuer MSAL-User im Email-Modus: Token manuell acquiren, da der
   * MsalInterceptor im Email-Modus deaktiviert ist (protectedResourceMap = null).
   */
  private async erstelleAuthHeaders(): Promise<HttpHeaders> {
    let headers = new HttpHeaders();

    // Dev-JWT als Bearer-Token: ermöglicht ladeUser() ohne MSAL/Supabase-Session.
    // Muss vor Supabase/MSAL stehen, da in Sandcastle-Tests kein echter Provider aktiv ist.
    if (!environment.production) {
      const devJwt = localStorage.getItem(DEV_JWT_KEY);
      if (devJwt) {
        return headers.set('Authorization', `Bearer ${devJwt}`);
      }
    }

    // Supabase-Token manuell setzen (MsalInterceptor kennt Supabase nicht)
    if (this.supabaseService.istEingeloggt) {
      const token = this.supabaseService.accessToken;
      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }

    // MSAL-Token manuell holen – noetig im Email-Modus, da der MsalInterceptor
    // dort deaktiviert ist (protectedResourceMap setzt apiUrl auf null).
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
      try {
        const result = await firstValueFrom(
          this.msalService.acquireTokenSilent({
            scopes: environment.msalConfig.scopes,
            account: accounts[0]
          })
        );
        if (result.accessToken) {
          headers = headers.set('Authorization', `Bearer ${result.accessToken}`);
        }
      } catch {
        // Silent-Token-Erwerb fehlgeschlagen (z.B. abgelaufener Token auf Mobile nach Inaktivitaet).
        // Interaktiven Login-Redirect ausloesen – die Seite wird dabei neu geladen.
        try {
          await firstValueFrom(
            this.msalService.acquireTokenRedirect({
              scopes: environment.msalConfig.scopes,
              account: accounts[0]
            })
          );
          // acquireTokenRedirect loest einen Seiten-Redirect aus,
          // dieser Code wird nur erreicht wenn der Redirect nicht stattfindet.
        } catch {
          // Auch interaktiver Login fehlgeschlagen – Guard leitet zu /login
          this._tokenFehler = true;
        }
      }
    }

    return headers;
  }

  /** True wenn der Token-Erwerb fehlgeschlagen ist (Login erforderlich). */
  private _tokenFehler = false;
}
